import { Injectable } from "@webmotors/ioc";
import { CRYPTO_SERVICE } from "./crypto.types";

import AES from "crypto-js/aes";
import Utf8 from "crypto-js/enc-utf8";

/**
 * Service for encrypting and decrypting data using AES encryption.
 */
@Injectable(CRYPTO_SERVICE)
export class CryptoService {
  /**
   * Encrypts a value using AES encryption with the provided secret key.
   *
   * @template T The type of the value to encrypt.
   * @param value - The value to encrypt. Can be a string or a serializable object.
   * @param secret - The secret key used for encryption.
   * @returns The encrypted string, or `null` if encryption fails.
   */
  encrypt<T>(value: T, secret: string): string | null {
    try {
      const valueToEncrypt =
        typeof value === "string" ? value : JSON.stringify(value);

      return AES.encrypt(valueToEncrypt, secret).toString();
    } catch (error) {
      return null;
    }
  }

  /**
   * Decrypts an encrypted string using AES encryption with the provided secret key.
   *
   * @param encrypted - The encrypted string to decrypt.
   * @param secret - The secret key used for decryption.
   * @returns The decrypted value as a string, or the raw decrypted string if JSON parsing fails.
   */
  decrypt<T>(encrypted: string, secret: string): T | string | null {
    let decrypted: string;

    try {
      decrypted = AES.decrypt(encrypted, secret).toString(Utf8);
    } catch (error) {
      return null;
    }

    try {
      return JSON.parse(decrypted) as T;
    } catch {
      return decrypted;
    }
  }
}
